import React, { useState } from "react";

//Component
import {
  DataGrid,
  GridColDef,
  GridColumns,
  GridToolbarContainer,
} from "@material-ui/data-grid";
import CustomLoadingOverlay from "components/DataGridLoadingOverlay";
import CustomNoRowsOverlay from "components/DataGridNoRowsOverlay";
import {
  ARIA_SuccessCode,
  ComponentType,
  PM_ADD_COMPONENT_TYPE,
  PM_DELETE_COMPONENT_TYPE,
  PM_EDIT_COMPONENT_TYPE,
} from "models";
import { DataGridFilterToolbar } from "components/DataGridFilterToolbar";
import { GridColFilterDef } from "components/DataGridFilterSelection";
//Icon

//Helper
import { convertDateTimeToString } from "utils/dateHelper";
import { Data } from "react-csv/components/CommonPropTypes";
import { ExportButton } from "components/ExportButton";
import { MainDrawer } from "components/MainDrawer";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { AccessControlButton, DataGridTypography } from "components";
import {
  createComponentTypeAPI,
  deleteComponentTypeAPI,
  getComponentTypeListAPI,
  updateComponentTypeAPI,
} from "services/componentTypeServices";

export const ComponentTypeManagementScreen = () => {

  const [index, setIndex] = useState<number>(1);
  const [size, setSize] = useState<number>(10);
  const [row, setRow] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [requestDateTime, setRequestDateTime] = useState<Date>(new Date());

  const [componentTypeList, setComponentTypeList] = useState<ComponentType[]>(
    []
  );
  const [exportList, setExportList] = useState<Data>([]);
  const [filterList, setFilterList] = useState<GridColFilterDef[]>([]);

  const [loadList, setloadList] = useState(true);

  const [addComponentType, setAddComponentType] = useState<any | undefined>();
  const [editComponentType, setEditComponentType] = useState<
    ComponentType | undefined
  >();
  const [removeComponentType, setRemoveComponentType] = useState<
    ComponentType | undefined
  >();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  //Setting up the each column header item
  const componentTypeNameHeader: GridColDef = {
    field: "name",
    headerName: "Name",
    renderHeader: (params: any) => <strong>{"Component Type Name"}</strong>,
    renderCell: (params: any) => {
      return <DataGridTypography content={params.value} />;
    },
    flex: 1,
    minWidth: 200,
  };

  const componentTypeCodeHeader: GridColDef = {
    field: "code",
    headerName: "Code",
    renderHeader: (params: any) => <strong>{"Code"}</strong>,
    renderCell: (params: any) => {
      return <DataGridTypography content={params.value} />;
    },
    flex: 1,
    minWidth: 160,
  };

  const componentTypeDescHeader: GridColDef = {
    field: "description",
    headerName: "Description",
    renderHeader: (params: any) => <strong>{"Description"}</strong>,
    renderCell: (params: any) => {
      return <DataGridTypography content={params.value} />;
    },
    flex: 2,
    minWidth: 240,
  };

  const actionsHeader: GridColDef = {
    field: "id",
    headerName: "Actions",
    minWidth: 240,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    renderHeader: () => <strong>{"Actions"}</strong>,
    renderCell: (params: any) => (
      <strong>
        <AccessControlButton
          code={PM_EDIT_COMPONENT_TYPE}
          variant="contained"
          color="primary"
          size="small"
          value={params.value}
          onClick={handleOpenEditDialog}
        >
          Edit
        </AccessControlButton>
        <AccessControlButton
          code={PM_ADD_COMPONENT_TYPE}
          variant="contained"
          color="primary"
          size="small"
          value={params.value}
          style={{ marginLeft: 12 }}
          onClick={handleDuplicateFunctionClick}
        >
          Clone
        </AccessControlButton>
        <AccessControlButton
          code={PM_DELETE_COMPONENT_TYPE}
          variant="contained"
          color="primary"
          size="small"
          value={params.value}
          style={{ marginLeft: 12 }}
          onClick={handleOpenDeleteDialog}
        >
          Delete
        </AccessControlButton>
      </strong>
    ),
  };

  const headerColumn: GridColumns = [
    componentTypeNameHeader,
    componentTypeCodeHeader,
    componentTypeDescHeader,
    actionsHeader,
  ];

  React.useEffect(() => {
    if (loadList) {
      setIsLoading(true);
      setloadList(false);
      setComponentTypeList([]);
      setRequestDateTime(new Date());

      getComponentTypeListAPI({
        filter: filterList,
        pageIndex: index,
        pageSize: size,
        orderBy: "name",
      })
        .then((response) => {
          if (response.resultCode === ARIA_SuccessCode) {
            setComponentTypeList(response.result.items);
            setRow(response.result.rowCount);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [index, size, filterList, loadList]);

  const getExportTransaction = async () => {
    setIsLoading(true);
    var _exportIndex = 1;
    var exportList: ComponentType[] = [];

    try {
      do {
        var response = await getComponentTypeListAPI({
          filter: filterList,
          pageIndex: _exportIndex,
          pageSize: size,
          orderBy: "name",
        });

        exportList.push(...response.result.items);

        _exportIndex++;
      } while (response.result.pageCount >= _exportIndex);

      setExportList(exportList);
      setIsExporting(true);
      setIsLoading(false);
    } catch (err) {
      console.log("Oposs.. Something is wrong");
    }
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <DataGridFilterToolbar
          headerColumns={headerColumn}
          filterList={filterList}
          onFilterApply={(items) => {
            setFilterList(items.slice());
          }}
        />
        <ExportButton
          filename="ComponentType"
          data={exportList}
          headers={[
            { label: "Name", key: "name" },
            { label: "Code", key: "code" },
            { label: "Description", key: "description" },
          ]}
          readyToExport={isExporting}
          onClick={getExportTransaction}
          onFinishDownload={() => {
            setExportList([]);
            setIsExporting(false);
          }}
        >
          Export
        </ExportButton>
        <div style={{ flexGrow: 1 }}></div>
        <AccessControlButton
          code={PM_ADD_COMPONENT_TYPE}
          variant="contained"
          color="primary"
          size="small"
          onClick={handleOpenCreateDialog}
        >
          New Component Type
        </AccessControlButton>
        <div style={{ paddingLeft: "8px" }}>
          <b>Data as at </b>: {convertDateTimeToString(requestDateTime)}
        </div>
      </GridToolbarContainer>
    );
  }

  const handleOpenCreateDialog = () => {
    setOpenEditDialog(true);
  };

  const handleDuplicateFunctionClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    var _updateComponentType = componentTypeList.find(
      (f) => f.id === parseInt(event.currentTarget.value)
    );

    setAddComponentType(_updateComponentType);
    setOpenEditDialog(true);
  };

  const handleOpenEditDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    var _updateComponentType = componentTypeList.find(
      (f) => f.id === parseInt(event.currentTarget.value)
    );

    setEditComponentType(_updateComponentType);
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    var _removeComponentType = componentTypeList.find(
      (f) => f.id === parseInt(event.currentTarget.value)
    );
    setRemoveComponentType(_removeComponentType);
    setOpenDeleteDialog(true);
  };

  const handleEditOrAdd = () => {
    if (editComponentType == null) {
      if (
        addComponentType === null ||
        addComponentType.name === null ||
        addComponentType.name === "" ||
        addComponentType.code === null ||
        addComponentType.code === ""
      ) {
        alert("Please fill up the details below to add a new Component Type.");
      } else {
        createComponentTypeAPI({
          ...addComponentType,
        })
          .then((response) => {
            if (response.resultCode == ARIA_SuccessCode) {
              setloadList(true);
              handleEditClose();
            } else {
              alert(response.resultDescription);
            }
          })
          .catch((err) => {
            alert(err);
          });
      }
    } else {
      if (
        editComponentType.name === null ||
        editComponentType.name === "" ||
        editComponentType.code === null ||
        editComponentType.code === ""
      ) {
        alert("Please fill up the details below to edit component type.");
      } else {
        updateComponentTypeAPI({
          id: editComponentType.id,
          name: editComponentType.name,
          code: editComponentType.code,
          description: editComponentType.description,
        })
          .then((response) => {
            if (response.resultCode == ARIA_SuccessCode) {
              setloadList(true);
              handleEditClose();
            } else {
              alert(response.resultDescription);
            }
          })
          .catch((err) => {
            alert(err);
          });
      }
    }
  };

  const handleDelete = () => {
    if (deleteConfirmation !== removeComponentType?.code) {
      alert("Please key in the component type code to confirm deletion.");
    } else {
      deleteComponentTypeAPI({ ...removeComponentType })
        .catch((err) => {
          alert(err);
        })
        .finally(() => {
          setDeleteConfirmation("");
          setloadList(true);
          handleEditClose();
        });
    }
  };

  const handleEditClose = () => {
    setAddComponentType(undefined);
    setEditComponentType(undefined);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  return (
    <>
      <MainDrawer title="Component Types">
        {/* Add and Edit Dialog */}
        <Dialog
          open={openEditDialog}
          onClose={handleEditClose}
          aria-labelledby="form-dialog-title"
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id="form-dialog-title">
            {editComponentType != undefined
              ? "Edit Component Type"
              : "Add Component Type"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {editComponentType != undefined
                ? "Change the detail below and save it to update component type details, ."
                : "Fill up the details below to add a new component type."}
            </DialogContentText>
            <form noValidate autoComplete="off">
              <div>
                <TextField
                  margin="dense"
                  id="name"
                  label="Component Type Name"
                  type="text"
                  value={
                    editComponentType != undefined
                      ? editComponentType.name
                      : addComponentType?.name != undefined
                      ? addComponentType?.name
                      : ""
                  }
                  onChange={(e) => {
                    editComponentType != undefined
                      ? setEditComponentType({
                          ...editComponentType,
                          name: e.currentTarget.value,
                        })
                      : setAddComponentType({
                          ...addComponentType,
                          name: e.currentTarget.value,
                        });
                  }}
                  fullWidth
                />
                <TextField
                  margin="dense"
                  id="code"
                  label="Code"
                  type="text"
                  value={
                    editComponentType != undefined
                      ? editComponentType.code
                      : addComponentType?.code != undefined
                      ? addComponentType?.code
                      : ""
                  }
                  onChange={(e) => {
                    editComponentType != undefined
                      ? setEditComponentType({
                          ...editComponentType,
                          code: e.currentTarget.value,
                        })
                      : setAddComponentType({
                          ...addComponentType,
                          code: e.currentTarget.value,
                        });
                  }}
                  fullWidth
                />
                <TextField
                  margin="dense"
                  id="desc"
                  label="Description"
                  type="text"
                  multiline
                  value={
                    editComponentType != undefined
                      ? editComponentType.description
                      : addComponentType?.description != undefined
                      ? addComponentType?.description
                      : ""
                  }
                  onChange={(e) => {
                    editComponentType != undefined
                      ? setEditComponentType({
                          ...editComponentType,
                          description: e.currentTarget.value,
                        })
                      : setAddComponentType({
                          ...addComponentType,
                          description: e.currentTarget.value,
                        });
                  }}
                  fullWidth
                />
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleEditOrAdd} color="primary">
              {editComponentType != null
                ? `Save & Update`
                : `Add Component Type`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* delete Dialog */}
        {removeComponentType != undefined ? (
          <Dialog
            open={openDeleteDialog}
            onClose={handleEditClose}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle id="form-dialog-title">
              Delete Component Type
            </DialogTitle>
            <DialogContent>
              <form noValidate autoComplete="off">
                <div>
                  <TextField
                    margin="dense"
                    id="name"
                    label="Component Type Name"
                    type="text"
                    disabled
                    value={removeComponentType.name}
                    fullWidth
                  />
                  <TextField
                    margin="dense"
                    id="code"
                    label="Code"
                    type="text"
                    disabled
                    value={removeComponentType.code}
                    fullWidth
                  />
                  <TextField
                    margin="dense"
                    id="description"
                    label="Description"
                    type="text"
                    disabled
                    multiline
                    value={removeComponentType.description}
                    fullWidth
                  />
                  <DialogContentText
                    color="textPrimary"
                    style={{ marginTop: "18px" }}
                  >
                    {`Are you sure you want to delete ${removeComponentType.name}?`}
                  </DialogContentText>
                  <DialogContentText>
                    Please type the following to confirm:
                  </DialogContentText>
                  <DialogContentText>
                    <i>
                      <b>{removeComponentType.code}</b>
                    </i>
                  </DialogContentText>
                  <TextField
                    margin="dense"
                    variant="outlined"
                    id="code"
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) =>
                      setDeleteConfirmation(e.currentTarget.value)
                    }
                    fullWidth
                  />
                </div>
              </form>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditClose} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDelete} color="primary">
                {`Delete`}
              </Button>
            </DialogActions>
          </Dialog>
        ) : null}

        {/* Data Grid */}
        <div style={{ height: "80vh" }}>
          <div style={{ display: "flex", height: "100%" }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                pageSize={size}
                rowsPerPageOptions={[10, 20, 30]}
                onPageSizeChange={(size) => {
                  setSize(size);
                  setloadList(true);
                }}
                disableColumnMenu
                density="compact"
                components={{
                  LoadingOverlay: CustomLoadingOverlay,
                  NoRowsOverlay: CustomNoRowsOverlay,
                  Toolbar: CustomToolbar,
                }}
                pagination
                loading={isLoading}
                paginationMode="server"
                rowCount={row}
                filterMode="server"
                onPageChange={(newPage) => {
                  setIndex(++newPage);
                  setloadList(true);
                }}
                rows={componentTypeList}
                columns={headerColumn}
                getRowId={(row) => row.id}
              />
            </div>
          </div>
        </div>
      </MainDrawer>
    </>
  );
};
